/**
 * DealActivityTimelinePremium - Timeline premium de atividades
 * Design inspirado no Bitrix24 com agrupamento por data, avatares e cards premium
 *
 * CHANGELOG:
 *   RELEASE: 2026-09-03 v6.0.0 - Evento de perda (status_change → lost) ganha linha
 *            secundária: bolinha na cor do motivo + nome do motivo e, quando houver,
 *            divisor "|" seguido da observação digitada no modal de perda. Dados vindos
 *            de new_value (enriquecido pela trigger log_deal_status_change v1.1.0).
 *   RELEASE: 2026-08-12 v5.3.0 - Número da proposta (badge "Proposta #N vX") movido da
 *            linha do título para a linha de detalhes, exibido após o badge de valor total.
 *   RELEASE: 2026-08-12 v5.1.0 - Renderiza eventos de PROPOSTA (origem 'proposal', gravados
 *            pela trigger tg_crm_proposal_deal_activity): ícone/cor semânticos por evento,
 *            badge "Proposta #N vX", valor total e motivo (auditoria). Visão unificada.

 *   RELEASE: 2026-08-09 v5.0.0 - Ícones clean (sem círculo/gradiente), cor semântica por
 *            tipo/módulo em coluna própria à esquerda do card; separador de data ajustado
 *            (pl-9). Paridade visual com a timeline do modal de clientes (v7.x).
 *   RELEASE: 2026-01-10 v3.0.0 - Redesign completo com agrupamento por data e avatares
 */

import React, { useRef, useEffect, useCallback } from 'react';
import { format, isPast, isFuture } from 'date-fns';
import { DollarSign } from 'lucide-react';
import { ptBR } from 'date-fns/locale';
import {
  FileText,
  Phone,
  Mail,
  Calendar,
  CheckSquare,
  ArrowRightLeft,
  User,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Trash2,
  CheckCircle2,
  Clock,
  XCircle,
  CalendarClock,
  AlertCircle,
  Plus,
  RotateCcw,
  Pencil,
  FileSignature,
  CreditCard,
  Hash,
  Tag,
  Flag,
  Link2,
  Unlink,
  BookOpen,
  ScrollText,
} from 'lucide-react';

// v1.3.0: eventos meta gerados pelo Playbook de Vendas que não correspondem a um
// activity_type do enum (são logados como 'note' + payload em new_value.source='playbook')
const PLAYBOOK_META_EVENTS = new Set<string>([
  'playbook_started',
  'playbook_cancelled',
  'playbook_completed',
  'playbook_reopened',
  'playbook_closed_by_status',
  'playbook_synced',
  'playbook_phase_added',
  'playbook_phase_skipped',
]);

// v5.4.0: metadados dos eventos de PROPOSTA passam a vir do módulo compartilhado
// (mesma aparência na timeline dos modais de Cliente/Contato).
import { PROPOSAL_EVENT_META } from '@/components/crm/shared/proposalActivityMeta';
// Card unificado de cobranças (avulsa/agrupada por assinatura)
import { ChargeActivityCard } from '@/components/crm/shared/ChargeActivityCard';




// v1.1.0: Labels traduzidos para prioridade da tarefa (system activity 'task')
const TASK_PRIORITY_LABELS: Record<string, string> = {
  low: 'Baixa',
  normal: 'Normal',
  medium: 'Média',
  high: 'Alta',
  urgent: 'Urgente',
};
import { useCrmDealActivities, ActivityType, DealActivity } from '@/hooks/useCrmDealActivities';
import { useDealActivitiesPaginated } from '@/hooks/useDealActivitiesPaginated';

import { ActivityAttachmentsDisplay } from '@/components/crm/deals/ActivityAttachmentsDisplay';
import { ActivityEditSheet } from './ActivityEditSheet';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { cn } from '@/lib/utils';
import googleMeetIcon from '@/assets/google-meet-icon.svg';
import { groupByDatePreserveOrder, DateGroup } from '@/lib/utils/group-by-date';
import { ActivityCommentSection } from '@/components/crm/activities/ActivityCommentSection';

// Props do componente
interface DealActivityTimelinePremiumProps {
  dealId: string;
  maxItems?: number;
  compact?: boolean;
  isActive?: boolean; // Para lazy loading
  /** v4.0.0: Ativa paginação server-side com scroll infinito */
  paginated?: boolean;
}

// Mapeamento de ícones por tipo de atividade
const activityIcons: Record<ActivityType, React.ComponentType<{ className?: string }>> = {
  note: FileText,
  call: Phone,
  email: Mail,
  meeting: Calendar,
  task: CheckSquare,
  phase_change: ArrowRightLeft,
  status_change: RefreshCw,
  assignment: User,
  value_change: TrendingUp,
  created: Plus,
  product_added: TrendingUp,
  product_removed: TrendingDown,
  contract_generated: FileSignature,
  charge_generated: CreditCard,
};

// v5.0.0: cor do ícone por tipo/módulo (sem círculo de fundo) — paridade com a timeline de clientes
const activityIconColors: Record<ActivityType, string> = {
  note: 'text-muted-foreground',
  call: 'text-emerald-600 dark:text-emerald-400',
  email: 'text-violet-600 dark:text-violet-400',
  meeting: 'text-orange-600 dark:text-orange-400',
  task: 'text-amber-600 dark:text-amber-400',
  phase_change: 'text-slate-500',
  status_change: 'text-slate-500',
  assignment: 'text-indigo-600 dark:text-indigo-400',
  value_change: 'text-teal-600 dark:text-teal-400',
  created: 'text-slate-500',
  product_added: 'text-teal-600 dark:text-teal-400',
  product_removed: 'text-rose-600 dark:text-rose-400',
  contract_generated: 'text-indigo-600 dark:text-indigo-400',
  charge_generated: 'text-teal-600 dark:text-teal-400',
};


// Labels de tipo de atividade
const activityLabels: Record<ActivityType, string> = {
  note: 'Nota',
  call: 'Ligação',
  email: 'Email',
  meeting: 'Reunião',
  task: 'Lembrete',
  phase_change: 'Mudança de fase',
  status_change: 'Mudança de status',
  assignment: 'Atribuição',
  value_change: 'Mudança de valor',
  created: 'Criado',
  product_added: 'Produto adicionado',
  product_removed: 'Produto removido',
  contract_generated: 'Contrato gerado',
  charge_generated: 'Cobrança gerada',
};

/**
 * Componente separador de data
 * Recebe padding à esquerda para não sobrepor a linha conectora
 */
function DateSeparator({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center my-4 pl-9">
      <div className="flex-1 h-px bg-border" />
      <span className="px-3 py-0.5 text-xs font-medium text-muted-foreground bg-muted rounded-full">
        {label}
      </span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

/**
 * Timeline premium de atividades do deal
 */
export function DealActivityTimelinePremium({ 
  dealId, 
  maxItems,
  compact = false,
  isActive,
  paginated = false
}: DealActivityTimelinePremiumProps) {
  // RELEASE: 2026-04-19 - Realtime: invalida cache da timeline quando crm_deal_activities muda
  // (cobre Resumo e aba Atividades — só ativo enquanto o modal estiver aberto)
  // v5.2.0: a assinatura realtime foi elevada para DealDetailsModalPremium,
  // garantindo que eventos de proposta cheguem mesmo com outra aba aberta.

  // RELEASE: 2026-02-09 v3.1.0 - Estado para atividade em edição
  const [editingActivity, setEditingActivity] = React.useState<DealActivity | null>(null);

  // RELEASE: 2026-02-13 - Estado para expandir/colapsar descrições longas na timeline
  const [expandedDescriptions, setExpandedDescriptions] = React.useState<Set<string>>(new Set());

  /** Alterna expansão da descrição de uma atividade */
  const toggleDescription = (id: string) => {
    setExpandedDescriptions(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Hook paginado (server-side) - sempre chamado mas desabilitado quando não paginado
  const paginatedResult = useDealActivitiesPaginated(dealId, { enabled: paginated && (isActive ?? true) });

  // Hook legado (client-side) - sempre chamado mas desabilitado quando paginado
  const legacyResult = useCrmDealActivities(dealId, { enabled: !paginated && (isActive ?? true) });

  // Seleciona resultado com base no modo
  const {
    activities: rawActivities,
    isLoading,
    deleteActivity,
    completeActivity,
    reopenActivity,
    updateActivity,
  } = paginated ? paginatedResult : legacyResult;

  // Limita items se necessário (apenas no modo legado)
  const displayedActivities = (!paginated && maxItems) ? rawActivities.slice(0, maxItems) : rawActivities;
  const hasMore = (!paginated && maxItems) ? rawActivities.length > maxItems : false;
  
  // Agrupa atividades por data
  const groupedActivities = groupByDatePreserveOrder(displayedActivities);

  // Ref do sentinela para IntersectionObserver (scroll infinito)
  const sentinelRef = useRef<HTMLDivElement>(null);

  /**
   * IntersectionObserver para disparar fetchNextPage no modo paginado
   */
  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && paginated && paginatedResult.hasNextPage && !paginatedResult.isFetchingNextPage) {
        paginatedResult.fetchNextPage();
      }
    },
    [paginated, paginatedResult.hasNextPage, paginatedResult.isFetchingNextPage, paginatedResult.fetchNextPage]
  );

  useEffect(() => {
    if (!paginated) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: '200px',
      threshold: 0,
    });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [paginated, handleIntersection]);

  // Verifica se atividade tem agendamento pendente
  // v1.4.0 — Notas não têm ciclo de conclusão
  const hasSchedulePending = (activity: DealActivity) => {
    if (activity.activity_type === 'note') return false;
    return activity.scheduled_at && !activity.completed_at;
  };

  // Verifica se agendamento está atrasado
  const isOverdue = (activity: DealActivity) => {
    if (activity.activity_type === 'note') return false;
    if (!activity.scheduled_at || activity.completed_at) return false;
    return isPast(new Date(activity.scheduled_at));
  };

  // Verifica se agendamento é futuro
  const isUpcoming = (activity: DealActivity) => {
    if (activity.activity_type === 'note') return false;
    if (!activity.scheduled_at || activity.completed_at) return false;
    return isFuture(new Date(activity.scheduled_at));
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex gap-3 p-4 rounded-xl border bg-card shadow-sm">
            <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-7 w-7 rounded-full" />
          </div>
        ))}
      </div>
    );
  }

  // Estado vazio
  if (rawActivities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <div className="h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center mb-3">
          <Clock className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium text-muted-foreground">
          Nenhuma atividade registrada
        </p>
        <p className="text-xs text-muted-foreground/70 mt-1">
          Adicione notas, ligações ou reuniões
        </p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      {/* Container principal (v1.4.0: sem linha conectora vertical) */}
      <div>

        
        <div className="space-y-1">
          {groupedActivities.map((group: DateGroup<DealActivity>, groupIndex: number) => (
            <div key={group.dateKey}>
              {/* Separador de data */}
              <DateSeparator label={group.dateLabel} />
              
              {/* Cards de atividades do grupo */}
              <div className="space-y-2">
                {group.items.map((activity) => {
                  const Icon = activityIcons[activity.activity_type] || FileText;
                  const isPending = hasSchedulePending(activity);
                  const overdue = isOverdue(activity);
                  const upcoming = isUpcoming(activity);
                  // v1.1.0: card de tarefa-sistema (vinculação/desvinculação) ganha estilo âmbar + bloco estruturado
                  const isTaskSystem = activity.is_system && activity.activity_type === 'task';
                  const taskNv = isTaskSystem ? (activity.new_value as Record<string, any> | null) : null;
                  // v1.2.0: diferencia visualmente vincular vs desvincular tarefa
                  const isUnlinked = isTaskSystem && taskNv?.action === 'unlinked';
                  const TaskActionIcon = isTaskSystem ? (isUnlinked ? Unlink : Link2) : Icon;
                  const taskAccentText = isUnlinked ? 'text-rose-700 dark:text-rose-400' : 'text-emerald-700 dark:text-emerald-400';
                  const taskAccentBlockBg = isUnlinked ? 'bg-rose-100/40 dark:bg-rose-900/20' : 'bg-emerald-100/40 dark:bg-emerald-900/20';

                  // v1.3.0: detecta eventos do Playbook de Vendas (origem 'playbook')
                  const playbookNv = activity.is_system
                    ? (activity.new_value as Record<string, any> | null)
                    : null;
                  const isPlaybook = playbookNv?.source === 'playbook';
                  const playbookEvent: string | undefined = playbookNv?.event;
                  const isPlaybookMeta = isPlaybook && playbookEvent ? PLAYBOOK_META_EVENTS.has(playbookEvent) : false;
                  const isPlaybookOverdue = isPlaybook && playbookEvent === 'step_overdue';
                  const PlaybookIcon = playbookEvent === 'step_completed'
                    ? CheckCircle2
                    : playbookEvent === 'step_skipped'
                      ? XCircle
                      : playbookEvent === 'step_overdue'
                        ? AlertCircle
                        : isPlaybookMeta
                          ? BookOpen
                          : ScrollText;

                  // v5.1.0: detecta eventos de PROPOSTA (origem 'proposal')
                  const proposalNv = activity.is_system
                    ? (activity.new_value as Record<string, any> | null)
                    : null;
                  const isProposal = proposalNv?.source === 'proposal';

                  // v6.0.0: detecta evento de PERDA (status_change para 'lost') para
                  // exibir motivo + observação em linha secundária. A trigger
                  // log_deal_status_change (v1.1.0+) enriquece new_value com nome/cor
                  // do motivo; eventos antigos podem ter apenas a observação.
                  const statusNv = activity.activity_type === 'status_change'
                    ? (activity.new_value as Record<string, any> | null)
                    : null;
                  const isLostEvent = statusNv?.status === 'lost';
                  // Nome do motivo: preferência pelo nome cadastrado; fallback p/ observação legada
                  const lossReasonName: string | null = statusNv?.loss_reason_name ?? null;
                  const lossReasonNote: string | null = statusNv?.loss_reason_note ?? null;
                  const lossReasonColor: string | null = statusNv?.loss_reason_color ?? null;
                  // Exibe a linha somente se houver o que mostrar
                  const hasLossDetail = isLostEvent && (!!lossReasonName || !!lossReasonNote);
                  const proposalMeta = isProposal
                    ? (PROPOSAL_EVENT_META[String(proposalNv?.event)] || PROPOSAL_EVENT_META.proposal_created)
                    : null;

                  const FinalIcon = isProposal && proposalMeta
                    ? proposalMeta.icon
                    : isPlaybook
                      ? PlaybookIcon
                      : TaskActionIcon;

                  // v5.0.0: cor semântica do ícone (sem círculo/gradiente de fundo)
                  const iconColorClass = isProposal && proposalMeta
                    ? proposalMeta.color
                    : isTaskSystem
                      ? (isUnlinked
                          ? 'text-rose-600 dark:text-rose-400'
                          : 'text-emerald-600 dark:text-emerald-400')
                      : isPlaybookOverdue
                        ? 'text-red-600 dark:text-red-400'
                        : isPlaybook
                          ? 'text-indigo-600 dark:text-indigo-400'
                          : (activityIconColors[activity.activity_type] || activityIconColors.note);

                  
                  return (
                    // v5.0.0: ícone clean em coluna própria + card ao lado (paridade com timeline de clientes)
                    <div key={activity.id} className="flex gap-2.5 group">
                      {/* Ícone do tipo de atividade (sem fundo) */}
                      <div className="h-8 w-6 flex items-center justify-center flex-shrink-0">
                        <FinalIcon className={cn('h-4 w-4', iconColorClass)} />
                      </div>

                    <div
                      className={cn(
                        "flex-1 min-w-0 rounded-xl border p-3 transition-all",
                        "shadow-sm hover:shadow-md hover:border-border",
                        // Destaque especial para agendamentos pendentes (mantém apenas a barra lateral colorida)
                        isPending && overdue && "border-l-3 border-l-red-400",
                        isPending && upcoming && "border-l-3 border-l-orange-400",
                        // v1.2.0: estilo de borda lateral para tarefa-sistema (vinculada/desvinculada)
                        isTaskSystem && !isUnlinked && "border-l-3 border-l-emerald-400",
                        isTaskSystem && isUnlinked && "border-l-3 border-l-rose-400",
                        // v1.3.0: destaque de borda lateral para eventos do Playbook
                        isPlaybookOverdue && "border-l-3 border-l-red-400",
                        isPlaybook && !isPlaybookOverdue && "border-l-3 border-l-indigo-400",
                        // v5.1.0: destaque semântico de borda lateral para eventos de Proposta
                        isProposal && proposalMeta && cn("border-l-3", proposalMeta.accent),

                        // Atividade concluída (mais sutil) — não aplica para notas
                        activity.activity_type !== 'note' && activity.completed_at && "opacity-75"
                      )}
                    >

                      
                      <div className="flex items-start gap-3">

                      {/* Coluna 2: Conteúdo principal */}
                      <div className="flex-1 min-w-0">
                        {/* Linha 1: Título + Badges */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className={cn(
                            "text-sm font-semibold",
                            activity.activity_type !== 'note' && activity.completed_at && "line-through text-muted-foreground"
                          )}>
                            {activity.title}
                            {/* v1.1.0: anexa #N da tarefa para tarefa-sistema */}
                            {isTaskSystem && taskNv?.task_number != null && (
                              <span className={cn("ml-1", taskAccentText)}>#{taskNv.task_number}</span>
                            )}
                          </h4>

                          {/* v5.2.0: autor no próprio título (paridade com a timeline de tarefas) */}
                          {activity.performer && (
                            <span className="text-[11px] text-muted-foreground">
                              (por: {activity.performer.full_name || 'Usuário'})
                            </span>
                          )}
                          

                          
                          {/* Badge de agendamento */}
                          {isPending && (
                            <Badge 
                              variant="outline"
                              className={cn(
                                "text-[10px] px-1.5 py-0.5 font-medium",
                                overdue 
                                  ? "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400" 
                                  : "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400"
                              )}
                            >
                              {overdue ? (
                                <AlertCircle className="h-3 w-3 mr-1" />
                              ) : (
                                <CalendarClock className="h-3 w-3 mr-1" />
                              )}
                              {format(new Date(activity.scheduled_at!), "dd/MM HH:mm")}
                            </Badge>
                          )}
                          
                          {/* Badge de sistema ou ícone Google Meet para agendamentos online */}
                          {activity.is_system && (
                            activity.activity_type === 'meeting' && activity.conferencing_link ? (
                              <Tooltip>
                                <TooltipTrigger>
                                  <img src={googleMeetIcon} alt="Google Meet" className="h-4 w-4" />
                                </TooltipTrigger>
                                <TooltipContent>Agendamento Online</TooltipContent>
                              </Tooltip>
                            ) : (
                              <Badge
                                variant="secondary"
                                className="text-[9px] px-1.5 py-0 h-4"
                              >
                                Sistema
                              </Badge>
                            )
                          )}

                          {/* v1.3.0: Badge "Playbook" para eventos da cadência (com tooltip do nome) */}
                          {isPlaybook && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge
                                  variant="outline"
                                  className="text-[9px] px-1.5 py-0 h-4 bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800 gap-1"
                                >
                                  <BookOpen className="h-2.5 w-2.5" />
                                  Playbook
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                {playbookNv?.playbook_name || 'Playbook de Vendas'}
                                {playbookNv?.step_title ? ` • ${playbookNv.step_title}` : ''}
                              </TooltipContent>
                            </Tooltip>
                          )}

                          {/* Status concluída/cancelada — apenas ícone colorido + texto preto, sem fundo. Notas não têm status. */}
                          {activity.activity_type !== 'note' && activity.completed_at && activity.description?.startsWith('❌ CANCELADO') ? (

                            <span className="inline-flex items-center gap-1 text-[10px] text-foreground">
                              <XCircle className="h-3 w-3 text-red-600 dark:text-red-400" />
                              Cancelado
                            </span>
                          ) : activity.activity_type !== 'note' && activity.completed_at ? (
                            <span className="inline-flex items-center gap-1 text-[10px] text-foreground">
                              <CheckCircle2 className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                              Concluída
                            </span>
                          ) : null}

                          {/* v5.2.0: data/hora alinhada à direita (mesmo padrão da timeline de tarefas) */}
                          <span className="ml-auto text-[11px] text-muted-foreground whitespace-nowrap">
                            {format(new Date(activity.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                          </span>
                        </div>


                        {/* v1.1.0: bloco estruturado âmbar para tarefa-sistema (substitui description) */}
                        {isTaskSystem ? (
                          <div className={cn("mt-1.5 rounded-md px-3 py-2 space-y-1 text-xs", taskAccentBlockBg)}>
                            <div className="flex items-center gap-1.5">
                              <Hash className={cn("h-3 w-3 flex-shrink-0", taskAccentText)} />
                              <span className="text-muted-foreground">N. Lembrete:</span>
                              <span className="font-medium text-foreground">#{taskNv?.task_number ?? '—'}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <FileText className={cn("h-3 w-3 flex-shrink-0", taskAccentText)} />
                              <span className="text-muted-foreground">Título:</span>
                              <span className="font-medium text-foreground truncate">{taskNv?.task_title || '—'}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Tag className={cn("h-3 w-3 flex-shrink-0", taskAccentText)} />
                              <span className="text-muted-foreground">Categoria:</span>
                              <span className="font-medium text-foreground">{taskNv?.category_name || '—'}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Flag className={cn("h-3 w-3 flex-shrink-0", taskAccentText)} />
                              <span className="text-muted-foreground">Prioridade:</span>
                              <span className="font-medium text-foreground">
                                {taskNv?.task_priority ? (TASK_PRIORITY_LABELS[taskNv.task_priority] || taskNv.task_priority) : '—'}
                              </span>
                            </div>
                          </div>
                        ) : (
                          /* Linha 2: Descrição (se houver) — demais tipos */
                          activity.description && (
                            <div className="mt-1.5">
                              {/* Wrapper com fundo cinza padrão da timeline de tarefas */}
                              <div
                                className={cn(
                                  "bg-muted/60 rounded-md px-3 py-2 text-xs text-foreground [&_*]:text-foreground cursor-pointer",
                                  !expandedDescriptions.has(activity.id) && "line-clamp-2"
                                )}
                                onClick={() => toggleDescription(activity.id)}
                                dangerouslySetInnerHTML={{ __html: activity.description }}
                              />
                              <button
                                type="button"
                                className="text-[11px] text-primary/70 hover:text-primary mt-0.5"
                                onClick={() => toggleDescription(activity.id)}
                              >
                                {expandedDescriptions.has(activity.id) ? 'Ver menos' : 'Ver mais'}
                              </button>
                            </div>
                          )
                        )}

                        {/* v6.0.0: linha secundária da perda — bolinha colorida do motivo
                            + nome e, com divisor "|", a observação (tooltip com texto cheio) */}
                        {hasLossDetail && (
                          <div className="mt-1 flex items-center gap-1.5 text-xs min-w-0">
                            {/* Bolinha na cor cadastrada do motivo (fallback: vermelho suave) */}
                            <span
                              className="h-2 w-2 rounded-full flex-shrink-0 ring-1 ring-black/10"
                              style={{ backgroundColor: lossReasonColor || '#EF4444' }}
                            />
                            {lossReasonName && (
                              <span className="font-medium text-foreground truncate">
                                {lossReasonName}
                              </span>
                            )}
                            {lossReasonNote && (
                              <>
                                {/* Divisor moderno entre motivo e observação */}
                                {lossReasonName && (
                                  <span className="text-muted-foreground/60 select-none">|</span>
                                )}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-muted-foreground truncate">
                                      {lossReasonNote}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs text-xs">
                                    {lossReasonNote}
                                  </TooltipContent>
                                </Tooltip>
                              </>
                            )}
                          </div>
                        )}

                        {/* v5.1.0: Detalhes enriquecidos para eventos de Proposta (valor total) */}
                        {/* v5.3.0: Número da proposta exibido após o valor */}
                        {isProposal && (
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {proposalNv?.total != null && (
                              <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0.5">
                                <DollarSign className="h-3 w-3" />
                                R$ {Number(proposalNv.total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </Badge>
                            )}
                            {proposalNv?.proposal_number && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex items-center gap-1 text-[10px] text-sky-700 dark:text-sky-300">
                                    <ScrollText className="h-3 w-3" />
                                    Proposta
                                    {` #${proposalNv.proposal_number}`}
                                    {proposalNv?.version_number ? ` v${proposalNv.version_number}` : ''}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {proposalNv?.proposal_title || 'Proposta comercial'}
                                </TooltipContent>
                              </Tooltip>
                            )}

                          </div>
                        )}


                        {/* Detalhes de cobrança gerada — card unificado compartilhado
                            (avulsa ou agrupada por assinatura, agregados vindos do backend) */}
                        {activity.activity_type === 'charge_generated' && (
                          <ChargeActivityCard
                            newValue={activity.new_value as Record<string, any> | null}
                          />
                        )}

                        {/* Anexos da atividade */}
                        {activity.attachments && activity.attachments.length > 0 && (
                          <div className="mt-2">
                            <ActivityAttachmentsDisplay 
                              attachments={activity.attachments} 
                              compact={compact}
                            />
                          </div>
                        )}

                        {/* Seção de comentários - oculta para atividades do sistema e modo compacto */}
                        {!compact && !activity.is_system && (
                          <div className="mt-2 pt-2 border-t border-border/50">
                            <ActivityCommentSection
                              activityId={activity.id}
                              commentsCount={activity.comments_count || 0}
                              compact={compact}
                            />
                          </div>
                        )}
                        {/* v5.2.0: rodapé de horário/autor removido — agora exibidos no título */}

                      </div>

                      {/* Coluna 3: Avatar do responsável + Ações */}
                      <div className="flex flex-col items-end gap-2">
                        {/* Avatar do responsável */}
                        {activity.performer && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div>
                                <SmartAvatar
                                  src={activity.performer.avatar_url}
                                  name={activity.performer.full_name}
                                  size="sm"
                                  className="border-2 border-background shadow-sm"
                                />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="text-xs">
                              {activity.performer.full_name || 'Usuário'}
                            </TooltipContent>
                          </Tooltip>
                        )}

                        {/* Ações (visíveis no hover) - apenas para não-sistema */}
                        {!activity.is_system && !compact && (
                          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            {/* RELEASE: 2026-02-09 v3.1.0 - Botão Editar (só se NÃO concluída) */}
                            {!activity.completed_at && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-muted-foreground hover:text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                                    onClick={() => setEditingActivity(activity)}
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                  Editar atividade
                                </TooltipContent>
                              </Tooltip>
                            )}

                            {/* Marcar como concluída (só se pendente e não-nota) */}
                            {activity.activity_type !== 'note' && !activity.completed_at && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-muted-foreground hover:text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
                                    onClick={() => completeActivity.mutate(activity.id)}
                                    disabled={completeActivity.isPending}
                                  >
                                    <CheckCircle2 className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                  Marcar como concluída
                                </TooltipContent>
                              </Tooltip>
                            )}

                            {/* Reabrir atividade (só se concluída e não-nota) */}
                            {activity.activity_type !== 'note' && activity.completed_at && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-muted-foreground hover:text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                                    onClick={() => reopenActivity.mutate(activity.id)}
                                    disabled={reopenActivity.isPending}
                                  >
                                    <RotateCcw className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                  Reabrir atividade
                                </TooltipContent>
                              </Tooltip>
                            )}

                            {/* Excluir */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => deleteActivity.mutate(activity.id)}
                                  disabled={deleteActivity.isPending}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-xs">
                                Excluir
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        )}
                      </div>
                      </div>
                      </div>
                    </div>

                  );
                })}
              </div>
            </div>
          ))}

          {/* Indicador de mais itens (modo legado) */}
          {hasMore && (
            <div className="text-center pt-3 pl-8">
              <span className="text-xs text-muted-foreground">
                +{rawActivities.length - (maxItems || 0)} atividades anteriores
              </span>
            </div>
          )}

          {/* Sentinela do IntersectionObserver para scroll infinito (modo paginado) */}
          {paginated && <div ref={sentinelRef} className="h-1" />}

          {/* Spinner de carregamento da próxima página */}
          {paginated && paginatedResult.isFetchingNextPage && (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="ml-2 text-xs text-muted-foreground">
                Carregando mais atividades...
              </span>
            </div>
          )}
        </div>
      </div>

      {/* RELEASE: 2026-02-09 v3.1.0 - Sheet de edição de atividade
          Montagem condicional: evita instanciar useCrmDealActivities enquanto fechado */}
      {editingActivity && (
        <ActivityEditSheet
          open={!!editingActivity}
          onOpenChange={(open) => !open && setEditingActivity(null)}
          activity={editingActivity}
          dealId={dealId}
          onSuccess={() => setEditingActivity(null)}
        />
      )}
    </TooltipProvider>
  );
}
